/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { KeyframeTrack } from 'three';
import { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';
import { OriginalDuration } from 'types';

import {
  FINGER_BONES,
  loadBVHFiles,
  manipulateRootMotion,
  quaternionsByOptionAndJoint,
  subclip,
} from '@cartwhl/3d/bvh';
import {
  FromStore,
  HAND_OPTIONS,
  RotationOffsets,
} from '@cartwhl/common/types';

export type AnimationData = {
  animationData: BVH;
  originalDuration: OriginalDuration;
  withOffsets?: RotationOffsets;
};

type RootMotion = [boolean, boolean, boolean];

type Config = {
  enableRootMotion?: RootMotion;
  shouldLoad?: boolean;
  allowRootTranslation?: boolean;
  staticHands?: HAND_OPTIONS;
  trimFrameStart?: number;
  trimFrameEnd?: number;
  yOffset?: number;
  rotationOffsets?: FromStore<RotationOffsets>;
};

const defaultConfig: Config = {
  enableRootMotion: [true, true, true],
  allowRootTranslation: true,
  shouldLoad: true,
  yOffset: 0,
  rotationOffsets: {
    left_ankle: [0, 0, 0],
    right_ankle: [0, 0, 0],
    left_collar: [0, 0, 0],
    right_collar: [0, 0, 0],
    left_hip: [0, 0, 0],
    right_hip: [0, 0, 0],
    left_shoulder: [0, 0, 0],
    right_shoulder: [0, 0, 0],
    neck: [0, 0, 0],
    head: [0, 0, 0],
  },
};

function filterTracks(animationData: BVH, allowRootTranslation?: boolean) {
  animationData.clip.tracks = animationData.clip.tracks.filter((track) => {
    return allowRootTranslation
      ? track.name.includes('quaternion') || track.name === 'pelvis.position'
      : track.name.includes('quaternion');
  });
}

function applyRootMotion(animationData: BVH, enableRootMotion?: RootMotion) {
  if (enableRootMotion) {
    const pelvisTrack = animationData.clip.tracks.find(
      (track) => track.name === 'pelvis.position',
    );
    if (pelvisTrack) {
      manipulateRootMotion(pelvisTrack as KeyframeTrack, ...enableRootMotion);
    }
  }
}

function applyStaticHands(animationData: BVH, staticHands?: HAND_OPTIONS) {
  if (staticHands) {
    animationData.clip.tracks.forEach((track) => {
      if (staticHands !== 'default') {
        if (FINGER_BONES[track.name]) {
          track.values = quaternionsByOptionAndJoint(
            staticHands as HAND_OPTIONS,
            track,
          );
        }
      }
    });
  }
}

function applyYOffset(animationData: BVH, yOffset?: number) {
  if (yOffset !== undefined && yOffset !== 0) {
    animationData.clip.tracks.forEach((track) => {
      if (track.name.includes('.position')) {
        // For position tracks, we need to adjust the Y component
        // The values array contains [x, y, z, x, y, z, ...] for each frame
        for (let i = 1; i < track.values.length; i += 3) {
          track.values[i] += yOffset;
        }
      }
    });
  }
}

function trimAnimationFrames(
  animationData: BVH,
  trimFrameStart?: number,
  trimFrameEnd?: number,
) {
  const trimStart = trimFrameStart ?? 0;
  if (trimFrameEnd !== undefined) {
    const { clip } = animationData;
    const originalFPS =
      clip.tracks[0].times.length > 1
        ? Math.round(1 / (clip.tracks[0].times[1] - clip.tracks[0].times[0]))
        : 24;

    animationData.clip = subclip(
      clip,
      clip.name,
      trimStart,
      trimFrameEnd + 1,
      originalFPS,
    );
  }
}

export const transformFilesToAnimationData = (
  BVHFiles: FromStore<BVH[]> | null,
  mergedConfig: Config,
) => {
  if (!BVHFiles) return;
  const animationDataArray = BVHFiles.map((animationData) => {
    const originalDuration = getOriginalDuration(animationData);

    // WARN: Clone does more than just clone,
    // it tries to reduce redundant knots, which may not be what we want.
    const clonedAnimationData: BVH = {
      skeleton: animationData.skeleton.clone(),
      clip: animationData.clip.clone(),
    };

    // Apply simple modifications
    filterTracks(clonedAnimationData, mergedConfig.allowRootTranslation);
    applyStaticHands(clonedAnimationData, mergedConfig.staticHands);
    applyRootMotion(clonedAnimationData, mergedConfig.enableRootMotion);
    applyYOffset(clonedAnimationData, mergedConfig.yOffset);
    trimAnimationFrames(
      clonedAnimationData,
      mergedConfig.trimFrameStart,
      mergedConfig.trimFrameEnd,
    );

    return {
      animationData: clonedAnimationData,
      originalDuration,
      withOffsets: mergedConfig.rotationOffsets,
    };
  });

  return animationDataArray;
};

function getOriginalDuration(animation: FromStore<BVH>): OriginalDuration {
  if (!animation) return { originalSeconds: 0, originalFrames: 0 };

  const clip = animation.clip;
  const originalFrames = clip.tracks[0].times.length;
  const originalSeconds = clip.duration;

  return { originalSeconds, originalFrames };
}

export default function useLoadAnimation(
  animationPaths: string[],
  setLoading?: (loading: boolean) => void,
  config: Config = {},
): Array<AnimationData> | null {
  const mergedConfig = useMemo(
    () => ({ ...defaultConfig, ...config }),
    [JSON.stringify(config)],
  );
  const [BVHFiles, setBVHFiles] = useState<BVH[] | null>(null);

  const loadAnimationFiles = async () => {
    if (!mergedConfig.shouldLoad) return;
    if (animationPaths.length === 0) return;
    setLoading?.(true);
    const BVHs = await loadBVHFiles(
      animationPaths,
      mergedConfig.rotationOffsets as RotationOffsets,
    );
    setLoading?.(false);
    setBVHFiles(BVHs);
  };

  useEffect(() => {
    loadAnimationFiles();
  }, [
    JSON.stringify(animationPaths),
    mergedConfig.shouldLoad,
    mergedConfig.rotationOffsets,
  ]);

  const animationData = useTransformFilesToAnimationData(
    BVHFiles,
    mergedConfig,
  );

  return animationData as AnimationData[] | null;
}

export function useTransformFilesToAnimationData(
  BVHFiles: FromStore<BVH[]> | null,
  mergedConfig: Config,
) {
  const animationData = useMemo(() => {
    if (!BVHFiles) return null;
    return transformFilesToAnimationData(BVHFiles, mergedConfig);
  }, [BVHFiles, JSON.stringify(mergedConfig)]);

  return animationData;
}
